import { useId, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { Figma, Github, Laptop, Shapes, X } from "lucide-react";
import { MENSAJE_ARCHIVO, anotarArchivo, type MotivoArchivo } from "../datos/materialesConocimiento";
import {
  MENSAJE_REPO,
  agregarSinRepetir,
  carpetaDeRutas,
  esArchivoFig,
  interpretarRepoGithub,
  quitarRecurso,
  recursoDeArchivo,
  recursoDeCarpeta,
  recursoDeRepo,
  type RecursoSistemaDiseno,
} from "../datos/sistemaDiseno";
import { useSoltarArchivos } from "../datos/useSoltarArchivos";
import { PieDePaso, type PropsPasoPuestaEnMarcha } from "./PieDePaso";
import { CLASE_FOCO } from "./tarjetasFuente";

/**
 * Paso 2 de la Puesta en marcha: EL SISTEMA DE DISEÑO.
 *
 * Sigue la forma de la pantalla equivalente de Claude Design, que fue el pedido
 * explícito: un campo para contar de qué se trata la marca, una tarjeta con
 * cuatro maneras de traer material (repo de GitHub, código de la computadora,
 * archivo de Figma, tipografías y assets) y un campo libre al final. En
 * castellano y con las piezas de Atlas — no es una traducción del HTML de allá:
 * los bordes, los radios, los focos y los botones salen de los mismos módulos
 * que el resto de la sección.
 *
 * TODO ES OPCIONAL, ASÍ QUE "CONTINUAR" NUNCA SE BLOQUEA. La tarjeta lo dice
 * con todas las letras en su título: quien no tenga sistema de diseño pasa de
 * largo. La regla del paso anterior (conexiones) no aplica acá.
 *
 * LO QUE NO HACE, QUE ES LO IMPORTANTE: no sube nada y no guarda nada. No hay
 * backend de sistema de diseño ni almacenamiento de archivos; de cada archivo se
 * anota lo que se puede leer sin subirlo (nombre, tamaño, formato) y de una
 * carpeta su nombre y cuántos archivos tiene. Todo vive en el estado de este
 * componente y se pierde al salir. La lógica de qué es un repo, qué es un
 * `.fig` y qué es una carpeta vive aparte y testeada en `datos/sistemaDiseno.ts`.
 *
 * Ver ADR-2026-08-17-estudio-marketing-paso-sistema-diseno.
 */

/**
 * La forma de un campo SIN margen, SIN alto y SIN COLOR DE BORDE. Los tres van
 * afuera a propósito: los dos usos (el textarea largo y el input de una línea)
 * los quieren distintos, y apilar `mt-0` sobre un `mt-2` NO lo pisa — entre dos
 * utilidades de Tailwind que compiten gana la que la hoja genera después, no la
 * que se escribe última en el `className`. Mismo motivo por el que
 * `tarjetasFuente.ts` separó su `CLASE_TARJETA_BASE`. El color del borde
 * también: el rojo del error no puede convivir con el gris en la misma clase.
 */
const CLASE_CAMPO_BASE =
  "w-full rounded-xl border bg-white px-4 text-[13px] leading-relaxed text-brand-black outline-none transition-colors placeholder:text-[#02062B]/[0.35] focus:border-[#1644EB]";
const CLASE_BORDE_CAMPO = "border-[#02062B]/[0.12]";
const CLASE_TEXTAREA = `${CLASE_CAMPO_BASE} ${CLASE_BORDE_CAMPO} mt-2 resize-none py-3`;
/** El de una línea, adentro de una fila de la tarjeta: sin margen (vive en una
 *  fila flex) y más bajo. El borde lo pone quien lo usa: cambia con el error. */
const CLASE_INPUT = `${CLASE_CAMPO_BASE} min-w-0 flex-1 py-2.5`;
const CLASE_ROTULO = "text-[13px] font-medium text-brand-black";
/** El "(o de tu sistema de diseño)", "(todo opcional)": aclaración dentro de un
 *  rótulo, en el mismo renglón pero sin el peso del rótulo. */
const CLASE_ACLARACION = "font-normal text-[#02062B]/[0.45]";
const CLASE_BOTON_CHICO = `shrink-0 rounded-xl border border-[#02062B]/[0.12] bg-white py-2.5 px-5 text-[13px] font-medium text-brand-black transition-colors hover:bg-[#F0F2F7] ${CLASE_FOCO}`;

/** Cuando se ARRASTRA una carpeta, el navegador entrega la carpeta como un
 *  "archivo" de 0 bytes: no hay forma de ver lo que tiene adentro. El buscador
 *  de carpetas sí lo permite, así que el mensaje manda ahí en vez de decir que
 *  el archivo está vacío (que es verdad y no sirve para nada). */
const MENSAJE_CARPETA_ARRASTRADA =
  "Para sumar una carpeta entera elegila con “buscá”: arrastrándola, el navegador no nos deja ver lo que tiene adentro.";

/* ── Piezas de la tarjeta ───────────────────────────────────────────────── */

/** Una fila de la tarjeta: el nombre de la opción a la izquierda y su control a
 *  la derecha, como en la pantalla que se copió. */
const Fila = ({
  icono,
  titulo,
  children,
}: {
  icono: ReactNode;
  titulo: string;
  children: ReactNode;
}) => (
  <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] items-center gap-5 py-4 px-5">
    <span className="flex min-w-0 items-center gap-2.5">
      <span
        aria-hidden="true"
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#02062B]/[0.08] bg-white text-[#1644EB]"
      >
        {icono}
      </span>
      <span className="text-[13.5px] font-semibold text-brand-black">{titulo}</span>
    </span>
    <div className="min-w-0">{children}</div>
  </div>
);

/** La aclaración que va DEBAJO de una fila, a todo el ancho de la tarjeta. */
const Nota = ({ children }: { children: ReactNode }) => (
  <p className="py-3 px-5 text-[12px] leading-relaxed text-[#02062B]/[0.5]">{children}</p>
);

const MensajeError = ({ id, children }: { id: string; children: ReactNode }) => (
  <p id={id} role="alert" className="mt-2 text-[12px] leading-relaxed text-[#C33B36]">
    {children}
  </p>
);

/**
 * La franja para soltar o elegir archivos.
 *
 * El manejo del arrastre sale de `useSoltarArchivos` —el mismo que usa la caja
 * grande del modal "Añadir fuente"—: lo que no puede fallar (que la pestaña no
 * navegue al archivo soltado, que el resaltado no se apague a mitad del gesto)
 * está resuelto en un solo lugar.
 */
const ZonaArchivos = ({
  texto,
  accept,
  carpeta = false,
  onArchivos,
}: {
  texto: string;
  accept?: string;
  /** Convierte el buscador de archivos en uno de CARPETAS. */
  carpeta?: boolean;
  onArchivos: (archivos: File[]) => void;
}) => {
  const { encima, props } = useSoltarArchivos(onArchivos);
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div
      {...props}
      className={`flex flex-wrap items-center justify-center gap-1.5 rounded-xl border border-dashed py-3 px-4 text-center text-[12.5px] transition-colors ${
        encima ? "border-[#1644EB] bg-[#1644EB]/[0.04]" : "border-[#02062B]/[0.16] bg-[#FAFBFD]"
      }`}
    >
      <span className="text-[#02062B]/[0.55]">{texto}</span>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={`rounded font-medium text-[#1644EB] underline underline-offset-2 ${CLASE_FOCO}`}
      >
        buscá
      </button>
      <input
        ref={(elemento) => {
          inputRef.current = elemento;
          // `webkitdirectory` no está en los tipos de React porque no es
          // estándar; se pone sobre el nodo. Es lo único que distingue un
          // buscador de archivos de uno de carpetas.
          if (elemento && carpeta) elemento.setAttribute("webkitdirectory", "");
        }}
        type="file"
        accept={accept}
        multiple
        className="hidden"
        onChange={(evento) => {
          onArchivos(Array.from(evento.target.files ?? []));
          // Sin esto, volver a elegir EXACTAMENTE el mismo archivo no dispara
          // otro `change` y la pantalla se queda como si no hubiera pasado nada.
          evento.target.value = "";
        }}
      />
    </div>
  );
};

/** Lo cargado en una fila. No se dibuja nada cuando está vacía: un "todavía no
 *  cargaste nada" repetido cuatro veces es ruido, no información. */
const ListaRecursos = ({
  recursos,
  etiqueta,
  onQuitar,
}: {
  recursos: RecursoSistemaDiseno[];
  etiqueta: string;
  onQuitar: (id: string) => void;
}) => {
  if (recursos.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1.5" aria-label={etiqueta}>
      {recursos.map((recurso) => (
        <li
          key={recurso.id}
          className="flex items-center gap-2 rounded-lg border border-[#02062B]/[0.08] bg-white py-2 px-3"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12.5px] font-medium text-brand-black">
              {recurso.nombre}
            </span>
            <span className="block truncate text-[11.5px] text-[#02062B]/[0.5]">
              {recurso.detalle}
            </span>
          </span>
          <button
            type="button"
            onClick={() => onQuitar(recurso.id)}
            // El nombre va en el rótulo accesible: con cuatro listas en la
            // pantalla, "Quitar" a secas no dice qué se está por sacar.
            aria-label={`Quitar ${recurso.nombre}`}
            className={`shrink-0 rounded-full p-1 text-[#02062B]/[0.4] transition-colors hover:bg-[#F0F2F7] hover:text-brand-black ${CLASE_FOCO}`}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </li>
      ))}
    </ul>
  );
};

/* ── Estado de cada fila ────────────────────────────────────────────────── */

interface ListaCargada {
  recursos: RecursoSistemaDiseno[];
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;
  /** Agrega lo leído. `rechazo` es lo que NO se pudo leer de esa misma tanda —
   *  gana sobre el mensaje de duplicado, porque es lo que el usuario no
   *  esperaba. */
  sumar: (nuevos: RecursoSistemaDiseno[], rechazo?: string | null) => void;
  quitar: (id: string) => void;
}

/**
 * Una de las cuatro listas de la tarjeta. Es un hook chico y no cuatro pares de
 * `useState` sueltos porque las cuatro hacen exactamente lo mismo: deduplicar
 * por id contra lo que ya está y decir por qué algo no entró.
 */
function useListaCargada(): ListaCargada {
  const [recursos, setRecursos] = useState<RecursoSistemaDiseno[]>([]);
  const [error, setError] = useState<string | null>(null);

  return {
    recursos,
    error,
    setError,
    sumar: (nuevos, rechazo) => {
      const resumen = agregarSinRepetir(recursos, nuevos);
      setRecursos(resumen.lista);
      // "Ya estaba" sólo cuando NO entró nada: si de cinco archivos entraron
      // cuatro, el mensaje sería un error sobre una carga que salió bien.
      const yaEstaba =
        resumen.nuevos.length === 0 && resumen.duplicados > 0 ? "Eso ya estaba en la lista." : null;
      setError(rechazo ?? yaEstaba);
    },
    quitar: (id) => {
      setRecursos((previos) => quitarRecurso(previos, id));
      setError(null);
    },
  };
}

/**
 * Anota una tanda de archivos. Reusa `anotarArchivo` de la base de conocimiento
 * (mismo id determinista, mismo formateo de tamaño) en vez de repetir esa
 * lógica: es el mismo problema, resuelto y testeado.
 */
function leerArchivos(archivos: File[]): {
  recursos: RecursoSistemaDiseno[];
  rechazo: string | null;
} {
  const recursos: RecursoSistemaDiseno[] = [];
  let motivo: MotivoArchivo | null = null;
  for (const archivo of archivos) {
    const leido = anotarArchivo({
      nombre: archivo.name,
      bytes: archivo.size,
      tipoMime: archivo.type,
    });
    if (leido.ok) {
      recursos.push(recursoDeArchivo(leido.material));
      continue;
    }
    // El primero que falló alcanza: los motivos posibles son dos y decir "y
    // otros 3 también" no cambia lo que el usuario tiene que hacer.
    motivo = motivo ?? leido.motivo;
  }
  return { recursos, rechazo: motivo ? MENSAJE_ARCHIVO[motivo] : null };
}

/* ── El paso ────────────────────────────────────────────────────────────── */

export const PasoSistemaDiseno = ({
  siguiente,
  onContinuar,
  anterior,
  onVolver,
}: PropsPasoPuestaEnMarcha) => {
  const idDescripcion = useId();
  const idNotas = useId();
  const idRepo = useId();
  const idErrorRepo = useId();
  const idErrorCodigo = useId();
  const idErrorFigma = useId();
  const idErrorAssets = useId();

  const [descripcion, setDescripcion] = useState("");
  const [notas, setNotas] = useState("");
  const [repoCrudo, setRepoCrudo] = useState("");

  const repos = useListaCargada();
  const codigo = useListaCargada();
  const figma = useListaCargada();
  const assets = useListaCargada();

  const anadirRepo = () => {
    const lectura = interpretarRepoGithub(repoCrudo);
    if (!lectura.ok) {
      repos.setError(MENSAJE_REPO[lectura.motivo]);
      return;
    }
    const recurso = recursoDeRepo(lectura.repo);
    // Se pregunta ANTES de agregar: después, `repos.recursos` todavía tiene el
    // valor viejo del render y la respuesta sería la de un estado que ya no es.
    const yaEstaba = repos.recursos.some((cargado) => cargado.id === recurso.id);
    repos.sumar([recurso]);
    // El campo se limpia sólo si el repo entró de verdad: con un duplicado,
    // borrarlo dejaría al usuario sin ver de cuál le estamos hablando.
    if (!yaEstaba) setRepoCrudo("");
  };

  const cargarCodigo = (archivos: File[]) => {
    // El buscador de carpetas devuelve TODOS los archivos de adentro, cada uno
    // con su ruta relativa: de ahí salen el nombre de la carpeta y el conteo.
    const carpeta = carpetaDeRutas(archivos.map((archivo) => archivo.webkitRelativePath));
    if (carpeta) {
      codigo.sumar([recursoDeCarpeta(carpeta)]);
      return;
    }
    // Sin rutas relativas fue un arrastre: o archivos sueltos (que se anotan
    // uno por uno) o una carpeta, que el navegador entrega vacía.
    const { recursos } = leerArchivos(archivos);
    if (recursos.length === 0) {
      codigo.setError(MENSAJE_CARPETA_ARRASTRADA);
      return;
    }
    codigo.sumar(recursos);
  };

  const cargarFigma = (archivos: File[]) => {
    const figs = archivos.filter((archivo) => esArchivoFig(archivo.name));
    const descartados = archivos.length - figs.length;
    const rechazo =
      descartados > 0
        ? `Acá va el archivo .fig que exporta Figma: dejamos afuera ${
            descartados === 1 ? "un archivo" : `${descartados} archivos`
          }. Las imágenes y las tipografías van en la fila de abajo.`
        : null;
    const { recursos, rechazo: rechazoLectura } = leerArchivos(figs);
    figma.sumar(recursos, rechazo ?? rechazoLectura);
  };

  const cargarAssets = (archivos: File[]) => {
    const { recursos, rechazo } = leerArchivos(archivos);
    assets.sumar(recursos, rechazo);
  };

  return (
    <div className="flex flex-1 flex-col">
      {/* Columna angosta y centrada: el formulario a todo el ancho de una
          pantalla de escritorio deja renglones ilegibles y controles perdidos a
          los costados. El pie, en cambio, va a todo el ancho — Matías quiere el
          "Continuar" en la esquina inferior derecha de la PANTALLA. */}
      <div className="mx-auto w-full max-w-[720px] flex-1 pt-8 pb-4">
        <div>
          <label htmlFor={idDescripcion} className={CLASE_ROTULO}>
            Nombre y descripción de tu marca{" "}
            <span className={CLASE_ACLARACION}>(o de tu sistema de diseño)</span>
          </label>
          <textarea
            id={idDescripcion}
            rows={3}
            value={descripcion}
            onChange={(evento) => setDescripcion(evento.target.value)}
            placeholder="Ej.: Nova Café — cafetería de especialidad en Palermo, con local a la calle y tienda online."
            className={CLASE_TEXTAREA}
          />
        </div>

        <section className="mt-8">
          <h2 className="text-[15px] font-semibold text-brand-black">
            Sumá ejemplos de tu sistema de diseño y de tus piezas{" "}
            <span className={CLASE_ACLARACION}>(todo opcional)</span>
          </h2>
          <p className="mt-1 text-[13px] leading-relaxed text-[#02062B]/[0.55]">
            Lo que mejor funciona: el código y los diseños de tu sistema, y las piezas que ya
            publicaste.
          </p>

          <div className="mt-4 divide-y divide-[#02062B]/[0.08] overflow-hidden rounded-2xl border border-[#02062B]/[0.08] bg-white">
            <Fila
              icono={<Github className="h-4 w-4" aria-hidden="true" />}
              titulo="Vincular código desde GitHub"
            >
              {/* `<form>` y no un botón suelto: en un campo de una sola línea,
                  Enter tiene que agregar el repo. Esta pantalla no tiene ningún
                  otro formulario, así que no hay envío que se pise. */}
              <form
                onSubmit={(evento) => {
                  evento.preventDefault();
                  anadirRepo();
                }}
                className="flex items-center gap-2"
              >
                <label htmlFor={idRepo} className="sr-only">
                  Link del repositorio de GitHub
                </label>
                <input
                  id={idRepo}
                  type="text"
                  value={repoCrudo}
                  onChange={(evento) => {
                    setRepoCrudo(evento.target.value);
                    // El error se va al primer cambio: dejarlo mientras el
                    // usuario corrige el link es acusarlo de algo que ya no
                    // escribió.
                    if (repos.error) repos.setError(null);
                  }}
                  placeholder="https://github.com/usuario/repo"
                  aria-invalid={repos.error ? true : undefined}
                  aria-describedby={repos.error ? idErrorRepo : undefined}
                  className={`${CLASE_INPUT} ${
                    repos.error ? "border-[#C33B36]" : CLASE_BORDE_CAMPO
                  }`}
                />
                <button type="submit" className={CLASE_BOTON_CHICO}>
                  Añadir
                </button>
              </form>
              {repos.error && <MensajeError id={idErrorRepo}>{repos.error}</MensajeError>}
              <ListaRecursos
                recursos={repos.recursos}
                etiqueta="Repositorios vinculados"
                onQuitar={repos.quitar}
              />
            </Fila>

            <Fila
              icono={<Laptop className="h-4 w-4" aria-hidden="true" />}
              titulo="Vincular código desde tu computadora"
            >
              <ZonaArchivos texto="Arrastrá una carpeta acá o" carpeta onArchivos={cargarCodigo} />
              {codigo.error && <MensajeError id={idErrorCodigo}>{codigo.error}</MensajeError>}
              <ListaRecursos
                recursos={codigo.recursos}
                etiqueta="Código de tu computadora"
                onQuitar={codigo.quitar}
              />
            </Fila>

            <Nota>
              No hace falta la aplicación entera: con la parte donde viven tus estilos y tus
              componentes alcanza.
            </Nota>

            <Fila
              icono={<Figma className="h-4 w-4" aria-hidden="true" />}
              titulo="Subir un archivo .fig"
            >
              <ZonaArchivos texto="Soltá el .fig acá o" accept=".fig" onArchivos={cargarFigma} />
              {figma.error && <MensajeError id={idErrorFigma}>{figma.error}</MensajeError>}
              <ListaRecursos
                recursos={figma.recursos}
                etiqueta="Archivos de Figma"
                onQuitar={figma.quitar}
              />
            </Fila>

            <Nota>
              Es el archivo que Figma exporta desde “Guardar copia local”, en el menú del archivo.
            </Nota>

            <Fila
              icono={<Shapes className="h-4 w-4" aria-hidden="true" />}
              titulo="Sumar tipografías, logos y assets"
            >
              <ZonaArchivos texto="Arrastrá archivos acá o" onArchivos={cargarAssets} />
              {assets.error && <MensajeError id={idErrorAssets}>{assets.error}</MensajeError>}
              <ListaRecursos
                recursos={assets.recursos}
                etiqueta="Tipografías, logos y assets"
                onQuitar={assets.quitar}
              />
            </Fila>
          </div>
        </section>

        <div className="mt-8">
          <label htmlFor={idNotas} className={CLASE_ROTULO}>
            ¿Algo más que quieras contarnos?
          </label>
          <textarea
            id={idNotas}
            rows={3}
            value={notas}
            onChange={(evento) => setNotas(evento.target.value)}
            placeholder="Ej.: usamos una paleta cálida y esquinas redondeadas. El tono es cercano pero profesional."
            className={CLASE_TEXTAREA}
          />
        </div>
      </div>

      {/* Nunca bloqueado: todo lo de esta pantalla es opcional. */}
      <PieDePaso
        habilitado
        motivo={null}
        siguiente={siguiente}
        onContinuar={onContinuar}
        anterior={anterior}
        onVolver={onVolver}
      />
    </div>
  );
};

export default PasoSistemaDiseno;
